import { createSelector, createSlice } from '@reduxjs/toolkit';
// Cap the retained console error buffer so it can't grow unbounded on a
// long-running kiosk (memory leak -> crash). Keeps only the most recent N.
const MAX_CONSOLE_ERRORS = 5;
// Initial state
const initialState = {
    popupRichMapID: null,
    allRooms: [],
    allDesks: [],
    POIs: {},
    POISNameMap: {},
    showOnMap: 'Rooms',
    isRoomFinderOpen: false,
    smartRooms: [],
    searchFilters: {
        all: false,
        meetingRooms: false,
        hotDesks: false,
        accessibleSpaces: false,
        smartRoom: false,
    },
    timeout: null,
    richMapLoaded: false,
    destinationRichMapId: null,
    destinationName: null,
    destinationFloorNumber: null,
    qrCodeRichMapId: null,
    checkIfModelReqFlag: false,
    directions: null,
    roomSearchPopUpOpen: false,
    showQRFrame: true,
    openRoomDetailsWithQR: false,
    resetWidgets: 0,
    clearPOIS: false,
    currentSlide: 1,
    consoleErrors: [],
    errorTrackingEnabled: true,
};
const selectPOISData = createSelector([(state) => state.POIs, (state) => state.POISNameMap], (POIS, POISNameMap) => ({ POIS, POISNameMap }));
// Slice to manage UI State
export const UISlice = createSlice({
    name: 'UI',
    initialState,
    reducers: {
        // Will set room details popup to show the given richMapID, (if null, popup will close)
        setRoomDetailsPopUp: (state, action) => {
            const { payload } = action.payload;
            state.popupRichMapID = payload;
        },
        setRoomDataFromMap: (state, action) => {
            const { payload } = action.payload;
            state.allRooms = payload;
        },
        setDeskDataFromMap: (state, action) => {
            const { payload } = action.payload;
            state.allDesks = payload;
        },
        setPOIDataFromMap: (state, action) => {
            const { payload } = action.payload;
            state.POIs = payload.POIs;
            state.POISNameMap = payload.POISNameMap;
        },
        setShowOnMap: (state, action) => {
            const { payload } = action.payload;
            state.showOnMap = payload;
        },
        setRoomFinderOpen: (state, action) => {
            const { payload } = action.payload;
            state.isRoomFinderOpen = payload;
        },
        setSmartRooms: (state, action) => {
            const { payload } = action.payload;
            state.smartRooms = payload;
        },
        setSearchFilters: (state, action) => {
            const { payload } = action.payload;
            state.searchFilters = payload;
        },
        setTimeoutId: (state, action) => {
            const { payload } = action.payload;
            state.timeout = payload;
        },
        setRichMapLoading: (state, action) => {
            const { payload } = action.payload;
            state.richMapLoaded = payload;
        },
        setDirectionsPanel: (state, action) => {
            var _a, _b;
            const { payload } = action.payload;
            // Backwards compatible: callers may pass either the raw rich map id string
            // (or null to clear), or an object carrying the authoritative destination
            // name/floor number captured at directions-start.
            if (payload && typeof payload === 'object') {
                state.destinationRichMapId = (_a = payload.richMapId) !== null && _a !== void 0 ? _a : null;
                state.destinationName = (_b = payload.name) !== null && _b !== void 0 ? _b : null;
                state.destinationFloorNumber =
                    typeof payload.floorNumber === 'number' &&
                        !Number.isNaN(payload.floorNumber)
                        ? payload.floorNumber
                        : null;
            }
            else {
                state.destinationRichMapId = payload !== null && payload !== void 0 ? payload : null;
                // A bare string / clear leaves the derived fields null so the stepper
                // falls back to parsing the rich map id (unchanged legacy behavior).
                state.destinationName = null;
                state.destinationFloorNumber = null;
            }
        },
        checkIfModelRequired: (state) => {
            // Toggle a flag, which will be caught by useEffect to check if model is to be placed or not on the floor
            state.checkIfModelReqFlag = !state.checkIfModelReqFlag;
        },
        setDirections: (state, action) => {
            const { payload } = action.payload;
            state.directions = payload;
        },
        setQRCodeRichMapId: (state, action) => {
            const { payload } = action.payload;
            state.qrCodeRichMapId = payload;
        },
        setRoomSearchPopUp: (state, action) => {
            const { payload } = action.payload;
            state.roomSearchPopUpOpen = payload;
        },
        setShowQRFrame: (state, action) => {
            const { payload } = action.payload;
            state.showQRFrame = payload;
        },
        setOpenRoomDetailsWithQR: (state, action) => {
            const { payload } = action.payload;
            state.openRoomDetailsWithQR = payload;
        },
        setResetWidgets: (state, action) => {
            const { payload } = action.payload;
            state.resetWidgets = payload;
        },
        clearPOISelection: (state) => {
            state.clearPOIS = !state.clearPOIS;
        },
        setCurrentSlide: (state, action) => {
            const { payload } = action.payload;
            state.currentSlide = payload;
        },
        pushConsoleError: (state, action) => {
            const { payload } = action.payload;
            state.consoleErrors.push(JSON.stringify(payload));
            if (state.consoleErrors.length > MAX_CONSOLE_ERRORS) {
                state.consoleErrors.shift();
            }
        },
        setErrorTracking: (state, action) => {
            const { payload } = action.payload;
            state.errorTrackingEnabled = payload;
        },
    },
    selectors: {
        getUIState: (state) => state,
        getAllRooms: (state) => state.allRooms,
        getAllDesks: (state) => state.allDesks,
        getPOIS: selectPOISData,
        getShowOnMap: (state) => {
            return state.showOnMap;
        },
        getRoomFinderStatus: (state) => state.isRoomFinderOpen,
        getSmartRooms: (state) => state.smartRooms,
        getSearchFilters: (state) => state.searchFilters,
        getTimeoutId: (state) => state.timeout,
        getRichMapLoaded: (state) => state.richMapLoaded,
        getDestinationRichMapId: (state) => state.destinationRichMapId,
        getDestinationName: (state) => state.destinationName,
        getDestinationFloorNumber: (state) => state.destinationFloorNumber,
        getCheckIfModelReqFlag: (state) => state.checkIfModelReqFlag,
        getDirections: (state) => state.directions,
        getQrCodeRichMapId: (state) => state.qrCodeRichMapId,
        getRoomDetailsRichMapId: (state) => state.popupRichMapID,
        getRoomSearchPopUpOpen: (state) => state.roomSearchPopUpOpen,
        getShowQRFrame: (state) => state.showQRFrame,
        getOpenRoomDetailsWithQR: (state) => state.openRoomDetailsWithQR,
        getResetWidgets: (state) => state.resetWidgets,
        getClearPOIS: (state) => state.clearPOIS,
        getCurrentSlide: (state) => state.currentSlide,
        getConsoleErrors: (state) => state.consoleErrors,
        getErrorTrackingFlag: (state) => state.errorTrackingEnabled,
    },
});
// Export actions
export const { setRoomDetailsPopUp, setRoomDataFromMap, setDeskDataFromMap, setPOIDataFromMap, setShowOnMap, setRoomFinderOpen, setSmartRooms, setSearchFilters, setTimeoutId, setRichMapLoading, setDirectionsPanel, checkIfModelRequired, setDirections, setQRCodeRichMapId, setRoomSearchPopUp, setShowQRFrame, setOpenRoomDetailsWithQR, setResetWidgets, clearPOISelection, setCurrentSlide, pushConsoleError, setErrorTracking, } = UISlice.actions;
// Export selectors
export const { getUIState, getAllRooms, getAllDesks, getPOIS, getShowOnMap, getRoomFinderStatus, getSmartRooms, getSearchFilters, getTimeoutId, getRichMapLoaded, getDestinationRichMapId, getDestinationName, getDestinationFloorNumber, getCheckIfModelReqFlag, getDirections, getQrCodeRichMapId, getRoomDetailsRichMapId, getRoomSearchPopUpOpen, getShowQRFrame, getOpenRoomDetailsWithQR, getResetWidgets, getClearPOIS, getCurrentSlide, getConsoleErrors, getErrorTrackingFlag, } = UISlice.selectors;
